import { AnchorHTMLAttributes, useId } from "react"
import { Card } from "react-bootstrap"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { exists } from "shared/lib/exists"
import { formatFileSize } from "shared/lib/file/formatFileSize"
import { stopPropagation } from "shared/lib/stopPropagation"
import { Action } from "shared/types/Action"
import { IconSize } from "shared/types/Icon"
import { WithClassName } from "shared/types/WithClassName"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"

import { IconName } from "../icon/__generated__/IconName"
import { LoadingSpinner } from "../loading-spinner/LoadingSpinner"
import "./styles/_file-card.scss"

const MAX_DISPLAY_FILENAME_LENGTH = 40

type FileCardStatus = "uploading" | "success" | "error"

export interface FileCardProps extends WithClassName {
  name: string
  extension: string
  url?: string
  fileSize?: number | null

  target?: AnchorHTMLAttributes<HTMLAnchorElement>["target"]

  status?: FileCardStatus

  actions?: Action[]
  onDelete?: () => void

  isReadonly?: boolean

  slot?: React.ReactNode
}

function shortenFileName(filename: string) {
  if (filename.length > MAX_DISPLAY_FILENAME_LENGTH) {
    return `${filename.slice(0, MAX_DISPLAY_FILENAME_LENGTH)}[...]`
  }
  return filename
}

function FileCardStatusIndicator({ status }: { status: FileCardStatus }) {
  const { t } = useTranslation()
  if (status === "uploading") {
    return (
      <span
        className={classNames(
          "file-card__status",
          "file-card__status--uploading"
        )}
      >
        <LoadingSpinner small /> <span>{t("Upload.InProgress")}</span>
      </span>
    )
  }

  if (status === "success") {
    return (
      <span
        className={classNames(
          "file-card__status",
          "file-card__status--success"
        )}
      >
        <Icon name={IconName.CHECK_CIRCLE} /> <span>{t("Upload.Success")}</span>
      </span>
    )
  }

  if (status === "error") {
    return (
      <span
        className={classNames("file-card__status", "file-card__status--error")}
      >
        <Icon name={IconName.CLOSE_CIRCLE} /> <span>{t("Upload.Error")}</span>
      </span>
    )
  }

  return null
}

function FileCardInfo({
  fileSize,
  status
}: {
  fileSize?: number | null
  status?: FileCardStatus
}) {
  const hasFileSize = exists(fileSize)
  const hasStatus = !!status

  if (!hasFileSize && !hasStatus) {
    return null
  }

  return (
    <span className="file-card__info">
      {hasFileSize ? (
        <span className="file-card__file-size">{formatFileSize(fileSize)}</span>
      ) : null}
      {hasStatus ? <FileCardStatusIndicator status={status} /> : null}
    </span>
  )
}

export function FileCard({
  name,
  extension,
  url,
  className,
  status,
  target,
  fileSize,
  actions,
  isReadonly,
  onDelete,
  slot
}: FileCardProps) {
  const fileNameId = useId()
  const { t } = useTranslation()
  const fullFileName = `${name}${extension ? `.${extension}` : ""}`

  return (
    <Card
      className={classNames(
        "file-card",
        "card--elevated",
        status ? `file-card--${status}` : undefined,
        className
      )}
    >
      <Card.Body>
        <Icon
          className="file-card__icon"
          name={IconName.FILE}
          size={IconSize.LARGE}
        />
        <div className="file-card__content">
          <span id={fileNameId} className="file-card__file">
            <span className="file-card__file-name">
              {shortenFileName(name)}
            </span>
            {extension ? (
              <span className="file-card__file-extension">.{extension}</span>
            ) : null}
          </span>
          <FileCardInfo fileSize={fileSize} status={status} />
        </div>
        {actions && actions.length > 0 && !(status === "uploading")
          ? actions.map(
              ({
                onClick,
                icon,
                label,
                disabled,
                className: actionClassName
              }) => (
                <Button
                  key={label}
                  variant={ButtonVariant.SECONDARY}
                  className={classNames("btn--borderless", actionClassName)}
                  onClick={onClick}
                  icon={icon}
                  label={label}
                  isIconOnly
                  disabled={disabled}
                />
              )
            )
          : null}
        {onDelete && !isReadonly && !(status === "uploading") ? (
          <Button
            label={`${fullFileName} ${t("General.Delete")}`}
            variant={ButtonVariant.SECONDARY}
            className="file-card__delete btn--borderless"
            onClick={onDelete}
            icon={IconName.BIN}
            isIconOnly
          />
        ) : null}

        {url && !status ? (
          <a
            className="btn btn-secondary btn--borderless file-card__download"
            href={url}
            download={name}
            title={name}
            target={target}
            onClick={stopPropagation}
            aria-labelledby={fileNameId}
          >
            <Icon name={IconName.DOWNLOAD} />
          </a>
        ) : null}
        {slot}
      </Card.Body>
    </Card>
  )
}
